/**
 * Admin Analytics Settings Page
 *
 * Configure Google Analytics, GTM, and Facebook Pixel.
 */

import type { FormEvent } from 'react';
import { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import type { AnalyticsSettings } from '@petmas/shared';
import { UI_CONSTANTS } from '@petmas/shared';
import { asAnalyticsSettings, toSettingsRecord } from '../../utils/typeAssertions';

export function AdminAnalyticsPage() {
  const { getSettings, updateSettings, isLoading, error: apiError } = useAdminApi();
  const [settings, setSettings] = useState<AnalyticsSettings>({
    ga4Enabled: false,
    ga4MeasurementId: null,
    gtmEnabled: false,
    gtmContainerId: null,
    fbPixelEnabled: false,
    fbPixelId: null,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSettings('analytics');
      if (data) {
        setSettings(asAnalyticsSettings(data));
      }
    } catch {
      setError('Error al cargar configuracion');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await updateSettings('analytics', toSettingsRecord(settings));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), UI_CONSTANTS.TOAST_DURATION_MS);
    } catch {
      setError('Error al guardar configuracion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof AnalyticsSettings, value: string | boolean | null) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Configuracion de Analytics</h2>
        <p className="text-gray-600 mb-6">
          Configura las integraciones de seguimiento para medir el comportamiento de los usuarios.
        </p>

        {(error ?? apiError) && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error ?? apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            Configuracion guardada exitosamente
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Google Analytics 4 */}
          <div className="border-b pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Google Analytics 4</h3>
                <p className="text-sm text-gray-500">Seguimiento de comportamiento y conversiones</p>
              </div>
              <Toggle
                enabled={settings.ga4Enabled}
                onChange={(enabled) => handleChange('ga4Enabled', enabled)}
              />
            </div>
            {settings.ga4Enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Measurement ID
                </label>
                <input
                  type="text"
                  value={settings.ga4MeasurementId ?? ''}
                  onChange={(e) => handleChange('ga4MeasurementId', e.target.value || null)}
                  placeholder="G-XXXXXXXXXX"
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Encuentra tu ID en Google Analytics {">"} Admin {">"} Data Streams
                </p>
              </div>
            )}
          </div>

          {/* Google Tag Manager */}
          <div className="border-b pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Google Tag Manager</h3>
                <p className="text-sm text-gray-500">Gestion centralizada de etiquetas</p>
              </div>
              <Toggle
                enabled={settings.gtmEnabled}
                onChange={(enabled) => handleChange('gtmEnabled', enabled)}
              />
            </div>
            {settings.gtmEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Container ID
                </label>
                <input
                  type="text"
                  value={settings.gtmContainerId ?? ''}
                  onChange={(e) => handleChange('gtmContainerId', e.target.value || null)}
                  placeholder="GTM-XXXXXXX"
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Encuentra tu ID en tagmanager.google.com
                </p>
              </div>
            )}
          </div>

          {/* Facebook Pixel */}
          <div className="pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Facebook Pixel</h3>
                <p className="text-sm text-gray-500">Seguimiento para anuncios de Facebook</p>
              </div>
              <Toggle
                enabled={settings.fbPixelEnabled}
                onChange={(enabled) => handleChange('fbPixelEnabled', enabled)}
              />
            </div>
            {settings.fbPixelEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pixel ID
                </label>
                <input
                  type="text"
                  value={settings.fbPixelId ?? ''}
                  onChange={(e) => handleChange('fbPixelId', e.target.value || null)}
                  placeholder="XXXXXXXXXXXXXXX"
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Encuentra tu ID en Facebook Business Suite {">"} Events Manager
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Toggle component
function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (enabled: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
        enabled ? 'bg-orange-500' : 'bg-gray-200'
      }`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
